import { TimePointV1 } from '../calendar/time-point.js';
import { CanonStatus, Capability, Library } from '../domain/enums.js';
import { z } from '../zod.js';
import { Fid, RowVersion, Uuid } from './common.js';
import { DocTitle, TypeKey } from './documents.js';
import { commaList, QueryBoolean, queryInt } from './query.js';
/**
 * The `@` picker (`search.picker`; checklist items 3 and 3b; A2.1 section 4's picker rule).
 * `typeKeys` is a comma-separated list of type keys (`npc,pc`); `capability` narrows to types
 * holding it (the speaker picker); `includeReference` adds the SRD group.
 */
export const PickerQueryV1 = z.strictObject({
    q: z.string().max(200),
    typeKeys: commaList('[a-z][a-z0-9_]{0,63}', 20).optional(),
    capability: Capability.optional(),
    limit: queryInt(1, 20).default(8),
    includeReference: QueryBoolean.optional(),
});
/** One picker row: matched on its title, or on an alias (`alias` holds the alias then). */
export const DocHitV1 = z.strictObject({
    id: Uuid,
    fid: Fid,
    title: DocTitle,
    typeKey: TypeKey,
    library: Library,
    isStub: z.boolean(),
    matched: z.enum(['title', 'alias']),
    alias: z.string().nullable(),
});
/**
 * Canon hits, then the SRD group (empty until 1.D's stage 2), and whether each group holds an
 * exact name match, which decides the highlighted row (A2.1 section 4, "Picker on an SRD-only
 * match").
 */
export const PickerResultV1 = z.strictObject({
    canon: z.array(DocHitV1),
    reference: z.array(DocHitV1),
    exact: z.strictObject({ canon: z.boolean(), reference: z.boolean() }),
});
/**
 * The palette, the Unlinked tab and Link (1.0b deliverable 18; asked by 1.D, its stage 1b).
 */
/** `search.palette` (Mod-K): `GET /api/search?q&limit`. */
export const PaletteQueryV1 = z.strictObject({
    q: z.string().max(200),
    limit: queryInt(1, 50).default(20),
});
/**
 * One palette row: matched on its title, an alias (`alias` holds it) or its text (`snippet` holds
 * the words around the hit). A pending page is found by its text, never by "Untitled". `when` (CS
 * r2c; DECISIONS 141 1.I-2 item 10, 150 1.Y item 3) is the page's in-world date, null when it has
 * none, which 1.Y shows on the row: optional until 1.D fills it in a short round after 1.L-2 stage 2.
 */
export const PaletteHitV1 = z.strictObject({
    id: Uuid,
    fid: Fid,
    title: DocTitle,
    titlePending: z.boolean(),
    typeKey: TypeKey,
    library: Library,
    isStub: z.boolean(),
    canonStatus: CanonStatus,
    matched: z.enum(['title', 'alias', 'text']),
    alias: z.string().nullable(),
    snippet: z.string().nullable(),
    when: TimePointV1.nullable().optional(),
});
/** Canon hits, then the SRD group: at most five hits and the total (A2.1 section 4). */
export const PaletteResultV1 = z.strictObject({
    canon: z.array(PaletteHitV1),
    reference: z.strictObject({
        hits: z.array(PaletteHitV1).max(5),
        total: z.number().int().nonnegative(),
    }),
});
/**
 * One place another page names this page in plain text (the Unlinked tab: canon only). An
 * occurrence is named by its block, its ordinal among the names in that block, and the body
 * version the list was read at.
 */
export const UnlinkedOccurrenceV1 = z.strictObject({
    sourceDocId: Uuid,
    sourceFid: Fid,
    sourceTitle: DocTitle,
    sourceTitlePending: z.boolean(),
    sourceTypeKey: TypeKey,
    sourceBodyVersion: RowVersion,
    blockId: Uuid,
    ordinal: z.number().int().nonnegative(),
    /** The words as written in the block. */
    text: z.string().min(1),
    /** The title or alias they match. */
    name: z.string().min(1),
    isAlias: z.boolean(),
    context: z.string(),
    inSecret: z.boolean(),
    inGmNote: z.boolean(),
});
/**
 * Link one occurrence ("Link", decision 31): the page of the path is the target. Stage 2c's
 * `hygiene.linkAll` takes the same item.
 */
export const MentionLinkRequestV1 = z.strictObject({
    sourceDocId: Uuid,
    blockId: Uuid,
    occurrence: z.strictObject({
        ordinal: z.number().int().nonnegative(),
        baseBodyVersion: RowVersion,
    }),
});
/** The source's body version after the link. */
export const MentionLinkResponseV1 = z.strictObject({ sourceBodyVersion: RowVersion });
//# sourceMappingURL=search.js.map